import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import SectionContainer from '../components/SectionContainer';
import PageHeader from '../components/PageHeader';
import { CatalogItem } from '../types';
import { getPublishedCatalogItems } from '../services/catalogService';

// Types
type ItemType = 'mobilier' | 'objet' | 'art-de-table' | 'all';

// Styled Components
const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
  gap: var(--spacing-sm);
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-text);
  background-color: ${props => props.$active ? 'var(--color-text)' : 'transparent'};
  color: ${props => props.$active ? 'var(--color-background)' : 'var(--color-text)'};
  font-family: var(--font-primary);
  font-size: 0.9rem;
  letter-spacing: 1px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.3s ease;
  margin: 0 var(--spacing-xs);

  &:hover {
    background-color: ${props => props.$active ? 'var(--color-text)' : 'rgba(0, 0, 0, 0.1)'};
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-lg);
  margin-top: var(--spacing-lg);
`;

const ItemCard = styled.div`
  border: 1px solid var(--color-light-gray);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  filter: none;
  transition: transform 0.3s ease;
  
  ${ItemCard}:hover & {
    transform: scale(1.03);
  }
`;

const ItemInfo = styled.div`
  padding: var(--spacing-md);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemTitle = styled.h3`
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
  &::first-letter {
    color: #C13221;
  }
`;

const ItemDescription = styled.p`
  font-size: 0.9rem;
  margin-bottom: var(--spacing-sm);
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

const ItemMeta = styled.div`
  margin-top: var(--spacing-sm);
  font-size: 0.8rem;
  color: var(--color-text-secondary);
`;

const ItemDetail = styled.div`
  margin-bottom: var(--spacing-xs);
  
  span {
    font-weight: 500;
    color: var(--color-text);
  }
`;

const ItemTypeTag = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 4px 8px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-right: var(--spacing-xs);
  background-color: var(--color-light-gray);
`;

const DetailsPage: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<ItemType>('all');
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCatalogItems = async () => {
      try {
        setLoading(true);
        const items = await getPublishedCatalogItems();
        setCatalogItems(items);
      } catch (err) {
        console.error('Error fetching catalog items:', err);
        setError('Unable to load catalog items. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogItems();
  }, []);

  const filteredItems = catalogItems.filter(item => {
    if (activeFilter === 'all') return true;
    return item.type === activeFilter;
  });

  const handleFilterChange = (filter: ItemType) => {
    setActiveFilter(filter);
  };

  return (
    // Espacement pour éviter chevauchement avec le header fixe
    <div style={{ paddingTop: 'var(--spacing-xl)' }}>
      <SectionContainer spacing="large">
        <PageHeader title="DÉTAILS" subtitle="Détails et finitions parfaites sont nos priorités." />
        
        {loading && <p>Chargement des éléments du catalogue...</p>}
        {error && <p>{error}</p>}
        
        {!loading && !error && (
          <ItemsGrid>
            {filteredItems.map(item => (
              <ItemCard key={item.id}>
                <ItemImage src={item.image} alt={item.title} />
                <ItemInfo>
                  <div>
                    <ItemTitle>{item.title}</ItemTitle>
                    <ItemDescription>{item.description}</ItemDescription>
                    <ItemMeta>
                      {item.designer && (
                        <ItemDetail>
                          <span><span style={{ color: '#C13221' }}>D</span>esigner:</span> {item.designer}
                        </ItemDetail>
                      )}
                      {item.material && (
                        <ItemDetail>
                          <span>Matériaux:</span> {item.material}
                        </ItemDetail>
                      )}
                      {item.dimensions && (
                        <ItemDetail>
                          <span><span style={{ color: '#C13221' }}>D</span>imensions:</span> {item.dimensions}
                        </ItemDetail>
                      )}
                    </ItemMeta>
                  </div>
                </ItemInfo>
              </ItemCard>
            ))}
          </ItemsGrid>
        )}
      </SectionContainer>
    </div>
  );
};

export default DetailsPage;
